import { useEffect, useMemo, useState } from 'react';

/**
 * Custom hook for creating a countdown timer.
 *
 * @param refreshIntervalMs - The interval (in milliseconds) at which to update the current time.
 * @returns An object containing various properties and methods related to the countdown timer.
 */
export const useTimer = (refreshIntervalMs = 250) => {
  const [endTime, setEndTime] = useState<Date | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  /**
   * Starts the countdown timer to a specific date.
   *
   * @param date - The end date of the countdown.
   */
  const startTimerToDate = (date: Date) => {
    setEndTime(new Date(date));
  };

  /**
   * Starts the countdown timer to the current time.
   */
  const startTimer = () => {
    setEndTime(new Date());
  };

  /**
   * Starts the countdown timer with a specific number of seconds.
   *
   * @param seconds - The number of seconds to count down from.
   */
  const startTimerWithTimeout = (seconds: number) => {
    const date = new Date();
    date.setSeconds(date.getSeconds() + seconds);
    setEndTime(date);
  };

  /**
   * Updates the current time at a specified interval.
   */
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, refreshIntervalMs);

    return () => clearInterval(timer);
  }, []);

  /**
   * Calculates and returns the number of seconds left until the end time.
   *
   * @returns The number of seconds left until the end time, or 0 if there is no end time.
   */
  const secondsLeft = useMemo(() => {
    if (!endTime) return 0;
    const endTimeMs = endTime?.getTime() || 0;
    const startTimeMs = currentTime?.getTime() || 0;
    return Math.floor((endTimeMs - startTimeMs) / 1000);
  }, [endTime, currentTime]);

  /**
   * Returns various properties and methods related to the countdown timer.
   *
   * @property {boolean} isRunning - Indicates whether the countdown timer is currently running.
   * @property {Function} startTimerToDate - Starts the countdown timer to a specific date.
   * @property {Function} startTimerWithTimeout - Starts the countdown timer with a specific number of seconds.
   * @property {number} secondsLeft - The number of seconds left until the end time.
   * @property {number} secondsPassed - The number of seconds passed since the start time (negative value).
   * @property {Function} startTimer - Starts the countdown timer to the current time.
   */
  return {
    isRunning: !!endTime,
    startTimerToDate,
    startTimerWithTimeout,
    secondsLeft,
    secondsPassed: secondsLeft ? -secondsLeft : 0,
    startTimer,
  };
};
